"use client";

import { useId, type ReactNode } from "react";
import { CloseIcon } from "../icons/CloseIcon";
import { ModalSheet } from "./ModalSheet";

type ModalSize = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";
type IconTone = "accent" | "muted" | "danger";

export type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  size?: ModalSize;
  /** Icon tile shown to the left of the title (38x38). */
  /**
   * Raise this dialog above the default modal layer (backdrop + sheet) when
   * it opens on top of another modal. Forwarded to ModalSheet.
   */
  elevated?: boolean;
  /** Small icon tile shown to the left of the title (32x32). */
  icon?: ReactNode;
  /** Tone for the icon tile — "accent" (solid accent fill) or "muted" (soft surface). */
  iconTone?: IconTone;
  /** Required dialog title — bold 15px. */
  title?: ReactNode;
  /** Subtitle line below title — 12px muted. */
  subtitle?: ReactNode;
  /** Accessible label for the close (X) button. */
  closeLabel?: string;
  /** Slot rendered before the icon/title (e.g. a back button on multi-view sheets). */
  headerLeading?: ReactNode;
  /** Slot rendered between the title block and the close X (e.g. a "+ Add" CTA). */
  headerTrailing?: ReactNode;
  /** When provided, replaces the icon + title + subtitle stack entirely (e.g. brand logo). */
  headerOverride?: ReactNode;
  /**
   * Accessible name for the dialog, for the one case the title cannot supply
   * it: a `headerOverride` (or a dialog with no `title` at all) leaves nothing
   * on screen to point `aria-labelledby` at.
   *
   * Do NOT pass this alongside a `title` to "improve" the name — the title node
   * is pointed at directly, so the name is the title, always, and a second
   * string here would only be a thing to keep in sync. It is ignored when a
   * title renders.
   */
  ariaLabel?: string;
  /** Status / hint slot on the left side of the footer (12px muted). */
  footerLeft?: ReactNode;
  /** Action buttons on the right side of the footer. */
  footerActions?: ReactNode;
  /** Extra classes for the scrollable body wrapper (e.g. a tinted background or custom padding). */
  bodyClassName?: string;
  /** Extra classes for the footer element (e.g. a matching background). */
  footerClassName?: string;
  /**
   * Mobile-only header rendered above the default header.
   * When provided: shown only on mobile (md:hidden), and the default
   * header becomes desktop-only (hidden md:flex).
   * When NOT provided: default header renders at all breakpoints (unchanged).
   */
  mobileHeader?: ReactNode;
  /**
   * Presentation. "sheet" (default) = bottom sheet on mobile, centered modal
   * on desktop. "drawer" = bottom sheet on mobile, full-height end-anchored
   * slide-in panel on desktop.
   */
  variant?: "sheet" | "drawer";
  /** Hide the footer (use when actions live inline in the body). */
  hideFooter?: boolean;
  /**
   * Conditionally render the footer. Pass `false` for sticky-when-dirty
   * patterns: `<Modal showFooterWhen={isDirty} ... />`.
   */
  showFooterWhen?: boolean;
  /**
   * Test hook, forwarded to the dialog panel through `ModalSheet`.
   *
   * Same reason it is declared there: an undeclared `data-testid` on a typed
   * prop bag is dropped rather than rendered, so callers were passing one that
   * never reached the DOM.
   */
  "data-testid"?: string;
  children: ReactNode;
};

/**
 * Reusable dialog with consistent chrome: icon + title + subtitle in the
 * header, scrollable body, and a left-status / right-actions footer. Built
 * on top of `ModalSheet` so it inherits the responsive sheet/modal behavior,
 * focus trap, scroll lock, escape-to-close, and entrance animation.
 */
export function Modal({
  isOpen,
  onClose,
  size = "lg",
  elevated = false,
  icon,
  iconTone = "accent",
  title,
  subtitle,
  closeLabel = "Close",
  headerLeading,
  headerTrailing,
  headerOverride,
  ariaLabel,
  mobileHeader,
  footerLeft,
  footerActions,
  bodyClassName,
  footerClassName,
  variant = "sheet",
  hideFooter = false,
  showFooterWhen,
  "data-testid": testId,
  children,
}: ModalProps) {
  const footerVisible =
    !hideFooter && (showFooterWhen === undefined || showFooterWhen);

  /*
   * The dialog's accessible name.
   *
   * `role="dialog"` with nothing naming it announces as a bare "dialog" — 57 of
   * the platform's 58 dialogs did, because `ModalSheet` spreads no props and no
   * call site COULD supply a name. The name is taken from the title node that
   * is already on screen rather than from a string prop: it cannot drift from
   * what the user sees, and CSS `text-overflow: ellipsis` plays no part in the
   * accessible-name computation, so a visually clipped title still yields the
   * COMPLETE name.
   *
   * `titleNamesDialog` mirrors the render condition below exactly. Pointing
   * `aria-labelledby` at an id that is not in the DOM is worse than pointing at
   * nothing — the name resolves to the empty string rather than falling back —
   * so the two must not drift apart.
   */
  const titleId = useId();
  const titleNamesDialog = !headerOverride && Boolean(title);

  const iconTileBase =
    "inline-flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[var(--radius-tile,9px)]";
  const iconTileClass =
    iconTone === "danger"
      ? `${iconTileBase} bg-[color:color-mix(in_oklab,var(--danger-500,var(--error-500))_13%,transparent)] text-[color:var(--danger-500,var(--error-500))]`
      : iconTone === "accent"
        ? `${iconTileBase} bg-[color:var(--accent-500)] text-[color:var(--on-accent,#fff)]`
        : `${iconTileBase} bg-[color:var(--surface-alt)] text-[color:var(--text-muted)]`;

  return (
    <ModalSheet
      isOpen={isOpen}
      size={size}
      onClose={onClose}
      elevated={elevated}
      variant={variant}
      data-testid={testId}
      labelledBy={titleNamesDialog ? titleId : undefined}
      ariaLabel={titleNamesDialog ? undefined : ariaLabel}
    >
      {mobileHeader ? <div className="md:hidden">{mobileHeader}</div> : null}
      <header
        data-ui="dialog-header"
        className={[
          "flex shrink-0 items-start gap-3 px-4 pb-3 pt-[15px]",
          mobileHeader ? "hidden md:flex" : "",
        ]
          .join(" ")
          .trim()}
      >
        {headerLeading ? (
          <span className="shrink-0">{headerLeading}</span>
        ) : null}
        {headerOverride ? (
          <div className="flex min-w-0 flex-1 items-center">
            {headerOverride}
          </div>
        ) : (
          <>
            {icon ? <span data-ui="dialog-icon" className={iconTileClass}>{icon}</span> : null}
            <div className="flex min-w-0 flex-1 flex-col gap-0.5 leading-tight">
              {/*
                DEFERRED — this `<span>` should be an `<h2>`, and is not yet.
                =============================================================
                What is held back: promoting the title to a real heading
                (`<h2 id={titleId} data-ui="dialog-title">` with these exact
                classes). That is the other half of the dialog-naming fix: the
                `aria-labelledby` below gives every dialog a NAME today, but
                until this is a heading a screen-reader operator still gets no
                rotor entry and no heading to skip to inside the sheet.

                Why it is held back, and not merely forgotten: a shared
                primitive is not done until `scripts/theme-parity/measure.mjs`
                reads `0 of N pairs differ` for `stack`, `homegrill` and
                `verde` (`apps/restaurant/AGENTS.md:229`). `<span>` → `<h2>`
                changes the browser's default heading margin and font sizing
                unless these classes fully override them — which is precisely
                what that script exists to catch — and the script cannot be run
                in this environment: it needs the Claude Design artboard
                `.dc.html` files, which are not in this repo
                (`apps/restaurant/scripts/theme-parity/README.md`).

                Before shipping the swap, in this order:
                  1. `node scripts/theme-parity/measure.mjs` from
                     `apps/restaurant`, with the artboards present — all three
                     looks at `0 of N pairs differ`, plus the negative control
                     (`apps/restaurant/AGENTS.md:138`: a detector that only
                     ever returns zero is indistinguishable from a broken one).
                  2. `scripts/style-override-detector.js` — 0 findings, 8
                     routes, at 390 and 1280.
                  3. Extend `dialogNaming.spec.tsx` (restaurant) with the
                     heading assertion it documents as deliberately absent.
                Keep `data-ui="dialog-title"` through the swap: it is the hook
                QA and the style-parity pairs key on.
              */}
              {title ? (
                <span id={titleId} data-ui="dialog-title" className="truncate text-[16px] font-bold text-[color:var(--text-primary)]">
                  {title}
                </span>
              ) : null}
              {subtitle ? (
                <span data-ui="dialog-subtitle" className="truncate text-[12.5px] text-[color:var(--text-muted)]">
                  {subtitle}
                </span>
              ) : null}
            </div>
          </>
        )}
        {headerTrailing ? (
          <span className="shrink-0">{headerTrailing}</span>
        ) : null}
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          data-ui="dialog-close"
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[var(--radius-tile,9px)] border border-[color:var(--border-light)] bg-[color:var(--surface)] text-[color:var(--text-muted)] transition-colors hover:bg-[color:var(--hover,var(--surface-alt))] hover:text-[color:var(--text-primary)]"
        >
          <CloseIcon size={16} />
        </button>
      </header>
      {/* Body content floats as a white inset card on the gray panel. */}
      <div
        data-ui="dialog-body"
        className={[
          "flex min-h-0 flex-1 px-3",
          footerVisible ? "" : "pb-3",
        ].join(" ")}
      >
        <div
          data-ui="dialog-card"
          className={[
            /*
             * `overflow-x-hidden` is not belt-and-braces, it is the fix for a
             * scrollbar nobody asked for. Per CSS, when ONE axis is not
             * `visible` the other computes to `auto` — so `overflow-y-auto`
             * alone gave this card a HORIZONTAL scrollbar too, and any child a
             * pixel wider than the body (a focus ring, a shadow, a rounded
             * button at the edge) made it appear on hover across the full width
             * of the dialog.
             *
             * Thin scrollbars on the vertical one for the same reason the chip
             * row got them: the platform default is a heavy rail inside a card
             * that is already small.
             */
            "flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overflow-x-hidden rounded-[var(--radius-panel,14px)] border-[length:var(--border-card-width,1px)] border-[color:var(--card-br,var(--border-light))] bg-[color:var(--surface)] p-[18px] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:rounded-full",
            bodyClassName ?? "",
          ].join(" ")}
        >
          {children}
        </div>
      </div>
      {footerVisible ? (
        <footer
          data-ui="dialog-footer"
          className={[
            "flex shrink-0 items-center gap-3 px-[18px] pb-4 pt-[13px]",
            footerClassName ?? "",
          ].join(" ")}
        >
          {footerLeft ? (
            <span className="min-w-0 text-[12px] text-[color:var(--text-muted)]">
              {footerLeft}
            </span>
          ) : null}
          <div className="ms-auto flex items-center gap-2.5">
            {footerActions}
          </div>
        </footer>
      ) : null}
    </ModalSheet>
  );
}
